import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { CurrentUserSelectors } from '@recruitee/account-data';
import { filterBoolean } from '@recruitee/common';
import type { Requisition } from '@recruitee/requisitions-types';
import { RequisitionStatus } from '@recruitee/requisitions-types';

import {
  RequisitionsAbilities,
  RequisitionsDataProviderService,
} from '../../data/single-requisition/requisitions-data-provider.service';
import { RequisitionsDialogsService } from '../../services/requisitions-dialogs.service';
import { RequisitionsSharedActionsService } from '../../services/requisitions-shared-actions.service';

@Injectable()
export class CreateJobFacade {
  private requisition: Requisition;
  private abilities: RequisitionsAbilities;
  private currentUserId: number;
  private hasAccessToAllJobs: boolean;
  private canManageJobs: boolean;
  private isRequisitionReadOnly: boolean;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    protected requisitionsData: RequisitionsDataProviderService,
    private requisitionsDialogsService: RequisitionsDialogsService,
    private requisitionsSharedActionsService: RequisitionsSharedActionsService,
    private store: Store,
  ) {
    requisitionsData.data$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(({ requisition, abilities, isRequisitionReadOnly }) => {
        this.requisition = requisition;
        this.abilities = abilities;
        this.isRequisitionReadOnly = isRequisitionReadOnly;
      });

    this.store
      .select(AbilitiesSelectors.abilityMap)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(enabledMap => {
        this.hasAccessToAllJobs = enabledMap[Ability.ViewAllJobs];
        this.canManageJobs = enabledMap[Ability.ManageJobs];
      });

    this.store
      .select(CurrentUserSelectors.data)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(admin => (this.currentUserId = admin.id));
  }

  public get canCreateJob(): boolean {
    return this.abilities.manageJobs && this.isAssigningJobsAllowed && !this.isRequisitionReadOnly;
  }

  public get isAssigningJobsAllowed(): boolean {
    return this.requisition.status === RequisitionStatus.Approved;
  }

  public handleShouldShowApprovedDialog(): void {
    const approvalRequestedData = this.requisition.approvalRequest;
    if (
      this.requisition.status === RequisitionStatus.Approved &&
      approvalRequestedData?.requesterId === this.currentUserId &&
      !approvalRequestedData?.viewedByRequester
    ) {
      this.requisitionsDialogsService
        .openRequisitionApprovedSuccessDialog(
          {
            canCreateJob: this.canManageJobs,
            requisitionName: this.requisition.title,
            offerSlug: this.requisition?.offers[0]?.slug || null,
            hasAccessToJob: this.hasAccessToAllJobs,
          },
          {
            requisitionId: this.requisition.id,
          },
        )
        .pipe(filterBoolean)
        .subscribe(({ createJob }) => {
          if (createJob) {
            this.requisitionsSharedActionsService.createJob(this.requisition.id, true);
          }
        });
    }
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
